import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';

import ActionButton from '../../../../components/Buttons/ActionButton';
import BasicPagination from '../../../../components/DataTable/BasicPagination';
import SearchInputField from '../../../../components/Forms/Input/SearchInputField';
import SelectField from '../../../../components/Forms/Input/SelectField';
import FormModal from '../../../../components/Modals/FormModal';
import useDebouncedValue from '../../../../hooks/useDebouncedValue';
import RestaurantRawMaterialCategoryService from '../../../../services/restaurant/RestaurantRawMaterialCategoryService';
import RestaurantRawMaterialService from '../../../../services/restaurant/RestaurantRawMaterialService';
import { PageMetadata } from '../../../../types/restaurant/PagedResponse';
import { RestaurantRawMaterialCategoryDTO } from '../../../../types/restaurant/RestaurantRawMaterialCategoryTypes';
import {
  RestaurantRawMaterialDTO,
  RestaurantRawMaterialForm,
} from '../../../../types/restaurant/RestaurantRawMaterialTypes';
import { IProductUnit } from '../../../../types/productUnit/productUnitInterfaces';
import RawMaterialQuickCreateModal from '../../purchases/RawMaterialQuickCreateModal';

interface Props {
  isOpen: boolean;
  branchId: number;
  units: IProductUnit[];
  selectedIds: number[];
  currentId: number | null;
  onClose: () => void;
  onSelect: (material: RestaurantRawMaterialDTO) => void;
}

const EMPTY_PAGE: PageMetadata = {
  number: 0,
  size: 20,
  totalElements: 0,
  totalPages: 0,
};

const PagedRawMaterialPicker = ({
  isOpen,
  branchId,
  units,
  selectedIds,
  currentId,
  onClose,
  onSelect,
}: Props) => {
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [items, setItems] = useState<RestaurantRawMaterialDTO[]>([]);
  const [metadata, setMetadata] = useState<PageMetadata>(EMPTY_PAGE);
  const [categories, setCategories] = useState<RestaurantRawMaterialCategoryDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickSaving, setQuickSaving] = useState(false);
  const debouncedSearch = useDebouncedValue(search, 350);

  const handleCreateCategory = async (name: string) => {
    try {
      const response = await RestaurantRawMaterialCategoryService.create(
        branchId,
        { name, description: '', icon: '', color: '', displayOrder: 1 }
      );
      setCategories((current) => [...current, response.data]);
      return response.data;
    } catch {
      return null;
    }
  };

  const handleQuickCreate = async (input: {
    name: string;
    categoryId: number;
    unitId: number;
  }) => {
    setQuickSaving(true);
    try {
      const form: RestaurantRawMaterialForm = {
        categoryId: input.categoryId,
        unitId: input.unitId,
        supplierId: '',
        code: '',
        name: input.name,
        description: '',
        minimumStock: '',
        currentStock: '',
        cost: '',
        barcode: '',
      };
      const response = await RestaurantRawMaterialService.create(branchId, form);
      setQuickOpen(false);
      onSelect(response.data);
    } finally {
      setQuickSaving(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    RestaurantRawMaterialCategoryService.listFiltered(branchId, {
      page: 0,
      size: 50,
      sort: ['name,asc'],
      filters: { search: null, active: true },
    }).then((response) => setCategories(response.content));
  }, [branchId, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    setLoading(true);
    RestaurantRawMaterialService.listFiltered(branchId, {
      page,
      size,
      sort: ['name,asc', 'id,asc'],
      filters: {
        search: debouncedSearch.trim() || null,
        categoryId: categoryId || null,
        unitId: null,
        supplierId: null,
        active: true,
      },
    })
      .then((response) => {
        if (!active) return;
        setItems(response.content);
        setMetadata(response.page);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [branchId, categoryId, debouncedSearch, isOpen, page, size]);

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Seleccionar materia prima"
      maxWidth="max-w-4xl"
    >
      <div className="space-y-4">
        <div className="flex justify-end">
          <ActionButton
            label="Crear materia prima"
            icon={<Plus className="h-4 w-4" />}
            variant="outline"
            size="sm"
            fullWidth={false}
            onClick={() => setQuickOpen(true)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <SearchInputField
            label="Buscar"
            name="ingredientMaterialSearch"
            value={search}
            iconKey="search"
            placeholder="Código o nombre..."
            withMargin={false}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
          />
          <SelectField
            label="Categoría"
            name="ingredientMaterialCategory"
            value={categoryId}
            iconKey="category"
            options={[
              { value: 0, label: 'Todas las categorías' },
              ...categories.map((category) => ({
                value: category.id,
                label: category.name,
              })),
            ]}
            onChange={(event) => {
              setCategoryId(Number(event.target.value));
              setPage(0);
            }}
          />
        </div>

        {loading ? (
          <div className="py-12 text-center text-sm text-bodydark2">Cargando materias primas...</div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-stroke py-12 text-center text-sm text-bodydark2 dark:border-strokedark">
            No se encontraron materias primas.
          </div>
        ) : (
          <div className="grid max-h-[390px] grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2">
            {items.map((material) => {
              const selected = selectedIds.includes(material.id);
              const isCurrent = currentId === material.id;
              return (
                <ActionButton
                  key={material.id}
                  label={`${material.code} · ${material.name}${selected ? ' · Seleccionada' : ''}`}
                  variant={isCurrent ? 'primary' : selected ? 'secondary' : 'outline'}
                  fullWidth
                  disabled={selected && !isCurrent}
                  onClick={() => onSelect(material)}
                  className="justify-start text-left"
                />
              );
            })}
          </div>
        )}

        <BasicPagination
          currentPage={metadata.number}
          totalPages={metadata.totalPages}
          totalElements={metadata.totalElements}
          size={metadata.size || size}
          first={metadata.number === 0}
          last={metadata.number >= metadata.totalPages - 1}
          onPageChange={setPage}
          onSizeChange={(nextSize) => {
            setSize(nextSize);
            setPage(0);
          }}
        />
      </div>

      <RawMaterialQuickCreateModal
        isOpen={quickOpen}
        categories={categories}
        units={units}
        saving={quickSaving}
        onClose={() => setQuickOpen(false)}
        onSubmit={handleQuickCreate}
        onCreateCategory={handleCreateCategory}
      />
    </FormModal>
  );
};

export default PagedRawMaterialPicker;
